import {
  Button,
  CenterProps,
  HStack,
  Image,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import { omit } from 'lodash';
import { DragEvent, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';

type Props = CenterProps & {
  name: string;
  defaultSrc?: string;
  onUpload: (file: File) => Promise<void>;
};

export const SimpleImageUpload = ({ name, defaultSrc, onUpload, ...rest }: Props) => {
  const value = useWatch({ name });

  const onDrop = (files: File[]) => {
    onUpload(files?.[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await onUpload(file);
  };

  const inputProps = getInputProps();

  const copy = useMemo(() => {
    return value ? 'Replace' : 'Browse';
  }, [value]);

  return (
    <Stack>
      <Stack direction="row" w="full">
        <Image
          src={value ?? defaultSrc}
          w="100px"
          h="full"
          borderRadius="lg"
          objectFit="contain"
          fallback={<Skeleton w="100px" h="full" />}
        />
        <Stack
          borderWidth="1px"
          borderColor="blue.300"
          borderStyle="dashed"
          borderRadius="lg"
          px="6"
          py="4"
          flexDirection="column"
          w="full"
          bg={useColorModeValue('blue.50', 'blue.800')}
          transition="all 0.2s"
          _hover={{
            cursor: 'pointer',
            bg: useColorModeValue('blue.100', 'blue.700'),
          }}
          {...getRootProps()}
          {...rest}
        >
          <Stack spacing="2" py="2" align="center" onDrop={handleDrop}>
            <VisuallyHiddenInput type="file" display="none" {...omit(inputProps, 'size')} />

            <Stack align="center" textAlign="center" spacing="1">
              <HStack spacing="1" align="center" whiteSpace="nowrap">
                <Text textStyle="body">Drop a file or click here to </Text>
                <Button variant="link" colorScheme="blue" size="lg" p="0">
                  {copy}
                </Button>
              </HStack>
              <Text textStyle="label">Supported Formats: Images</Text>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
